/**
 * The task vocabulary, and the entity every `tasks_*` tool answers with.
 *
 * `ARCHITECTURE.md` §The task queue is the source: the queues, the states, what a
 * task carries, and the rule that `overdue` is derived from `due_at` and never
 * stored. `packages/shared/src/states.ts` owns the state machine; nothing here
 * restates it.
 *
 * It lives in `packages/shared` for the reason `.docs/00-index.md` §5 gives — "API
 * validates and SPA forms resolve with the same object" — and because three
 * different readers need the same list of queues: `agent.queues` (session 05), the
 * `task.queue` column, and session 20's board filter.
 */
import { z } from 'zod';
import { REF_KINDS } from '../refs.js';
import { taskMachine } from '../states.js';
import { MoneySchema, Rfc3339Schema } from './primitives.js';
import { TaskRefSchema } from './refs.js';
/* --------------------------------------------------------------- the queues */
/**
 * `ARCHITECTURE.md` §The task queue: "Queues: `delivery`, `content`, `seo`, `odoo`,
 * `reports`, `account`, `finance`. An agent subscribes to the queues it can do.
 * `account` and `finance` are human by default."
 *
 * "Human by default" is not a property of the list: it is `.docs/00-index.md` §6's
 * agent default — a new agent is created with **no** queues — plus `tasks.queuesFor`,
 * which hands an agent only the queues its owner put on its own row. Nothing here
 * marks two of the seven, because nothing reads such a mark: a person works any of
 * them, and an agent works what it was given.
 *
 * The list arrived in session 05 as part of `schemas/agents.ts`, which needed it for
 * `agent.queues`; session 07 moved it here, where the thing it is a property of
 * lives. `schemas/agents.ts` imports `TaskQueueSchema` from this file.
 */
export const TASK_QUEUES = [
    'delivery',
    'content',
    'seo',
    'odoo',
    'reports',
    'account',
    'finance',
];
export const TaskQueueSchema = z
    .enum(TASK_QUEUES)
    .describe('A work queue. delivery, content, seo and odoo are production work; reports is the ' +
    'monthly client report; account and finance are the two a person normally holds ' +
    '(ARCHITECTURE.md, The task queue).');
/* ------------------------------------------------- what the client may read */
/**
 * The queues a client never sees a task from — session 43's one visibility rule.
 *
 * Ferit's own answer, 22 September 2026: "every task filed against the client,
 * except billing-related ones." This is that sentence as data, and it is an
 * **exclusion** rather than an allow-list for the reason the exclusion is total:
 * `task.queue` is `not null` under a CHECK (`task_queue_known`), so every task is in
 * exactly one of the seven and a queue added next year has to be classified here or
 * the `satisfies` below stops compiling. There is no migration and no back-fill —
 * every task already written is already classified.
 *
 * The two are the two `TaskQueueSchema` above already calls "the two a person
 * normally holds", and what they hold is the studio's business *about* an account
 * rather than work *for* it: `account` is where `Card declined — call them`, `Pause
 * work for <client>` and `Chase sign-off on <file>` live, and `finance` is `Match
 * payment <step_id>` (`ARCHITECTURE.md` §The task queue, and `seed/task-rules.ts`).
 *
 * Excluding `account` does a second thing worth knowing, because a later reader will
 * otherwise wonder why "waiting on the client" is not a client-facing state: all
 * three `decision: true` rule seeds are `account` rows, and `clientDecisions` already
 * states every one of them in "Things to do" from `work_orders` and `retainers`. The
 * exclusion is therefore also what stops the dashboard stating one decision twice.
 *
 * **Rejected**, and recorded so the arguments are not had again: `service_code is not
 * null`, which is narrower than what was asked and lets "Odoo upgrade — quote sent,
 * no answer" through; and a `client_visible` column, which is a migration, a
 * back-fill of every existing row, and one more thing every caller of `tasks.create`
 * has to remember — which is how the portal came to have no client-facing task
 * surface in the first place.
 */
export const CLIENT_HIDDEN_QUEUES = ['account', 'finance'];
/**
 * What a client reads where the studio reads a status. Decided here, on the server.
 *
 * A narrowing rather than a rendering, which is why it is not the SPA's: five studio
 * states become three client words, and `review` becoming "In progress" is a
 * **policy** — whether the studio has finished something and is checking it is not
 * the client's business, and a page that mapped the states itself would be a page
 * deciding that. `useFormat` spells months and money; it does not decide what a
 * client is told about internal review.
 *
 * Total over the live states, so a state added to `taskMachine` is a type error here
 * rather than a row that silently reads as nothing. `done` and `cancelled` are
 * absent because no closed task is ever in flight — a completed one reaches the
 * client as "delivered recently" and a cancelled one reaches them not at all.
 *
 * `blocked` is deliberately **one word for every reason**: `blocked_reason` is prose
 * written for a colleague ("the account is fourteen days past due") and never
 * crosses, and `waiting_on: 'client'` is not read either — with the reason withheld
 * it would state an ask without saying what it is. Both are in the session report as
 * the shape of the eventual answer rather than as an oversight.
 */
export const CLIENT_TASK_STAGES = {
    open: 'Queued',
    claimed: 'In progress',
    in_progress: 'In progress',
    review: 'In progress',
    blocked: 'On hold',
};
/**
 * The client's word for a task's state, or `''` for a closed one.
 *
 * Empty rather than a throw: `ClientWorkItemSchema.stage` already declares an empty
 * stage "a real state and not an error", and a read model that threw on a task which
 * reached `done` between its own two statements would take the whole dashboard with
 * it.
 */
export function clientTaskStage(status) {
    return CLIENT_TASK_STAGES[status] ?? '';
}
/* -------------------------------------------------------------- the pieces */
/** 1 is the most urgent. Five bands, because a scale with more is a scale nobody uses. */
export const TASK_PRIORITY_MIN = 1;
export const TASK_PRIORITY_MAX = 5;
export const DEFAULT_TASK_PRIORITY = 3;
export const TaskPrioritySchema = z
    .int()
    .min(TASK_PRIORITY_MIN)
    .max(TASK_PRIORITY_MAX)
    .describe('How urgent, 1 to 5, where 1 is the most urgent. claim_next hands out the lowest ' +
    'number first, then the earliest due date. 3 is normal.');
/** Who a task is assigned to. `none` is the queue itself: nobody has picked it up. */
export const ASSIGNEE_KINDS = ['user', 'agent', 'none'];
export const AssigneeKindSchema = z
    .enum(ASSIGNEE_KINDS)
    .describe('Who holds it: a person (user), an agent, or nobody yet (none).');
/**
 * `ARCHITECTURE.md` §The task queue: "`tasks_block` records `waiting_on: client |
 * staff | external` and a reason — the ochre 'not our move' state in the studio."
 */
export const WAITING_ON = ['client', 'staff', 'external'];
export const WaitingOnSchema = z
    .enum(WAITING_ON)
    .describe('Whose move it is while this is blocked: client (they owe us an approval, footage or ' +
    'access), staff (somebody here does), or external (a third party — a platform, a bank).');
/** What a time entry draws on — the retainer, a project, or the studio's own account. */
export const BILLABLE_KINDS = ['retainer', 'project', 'account'];
export const BillableKindSchema = z
    .enum(BILLABLE_KINDS)
    .describe('What the time draws on: retainer (inside a package), project (a work order) or ' +
    'account (re-scoping, billing chases, policy calls — work the studio absorbs).');
/**
 * The kinds of thing a finished task can point at.
 *
 * Every reference kind except `client` — a slug is not something a task produces —
 * plus `file`, which is the one deliverable with an id rather than a ref
 * (`ARCHITECTURE.md` §Files).
 */
export const TASK_OUTPUT_KINDS = [
    ...REF_KINDS.filter((kind) => kind !== 'client'),
    'file',
];
/**
 * The payload a review task carries: the call that was not made.
 *
 * `.docs/07-tasks.md` deliverable 2: "the prepared payload in `outputs` as `{ kind:
 * 'prepared_action', action, input }`". Reviewing it is a person reading it and then
 * making the call themselves — completing the review task does **not** run it, which
 * is why the instructions say so in words as well.
 */
export const PREPARED_ACTION_KIND = 'prepared_action';
const PreparedActionOutputSchema = z.strictObject({
    kind: z.literal(PREPARED_ACTION_KIND).describe('A tool call that was prepared and not made.'),
    action: z.string().describe('The tool that was refused, by name — "invoices_issue".'),
    input: z
        .record(z.string(), z.unknown())
        .describe('The arguments it would have been called with, exactly as they were sent.'),
    ran_at: Rfc3339Schema.optional().describe('When a person made this call — from the task’s own page, or through the tool with ' +
        'exactly these arguments while the task was in review. Absent until then, and ' +
        'written once: a held call is made one time only.'),
});
const RefOutputSchema = z.strictObject({
    kind: z.enum(TASK_OUTPUT_KINDS).describe('What sort of thing it is.'),
    ref: z.string().describe('Its reference, or its id when it is a file.'),
});
export const TaskOutputSchema = z
    .discriminatedUnion('kind', [PreparedActionOutputSchema, RefOutputSchema])
    .describe('One thing this task produced, or the prepared call a review task is about.');
/* ------------------------------------------------------------- the entities */
/**
 * A task, as every tool answers with it.
 *
 * `ARCHITECTURE.md` §Guardrails: "Every result is the whole updated entity, so an
 * agent does not need a follow-up read." Every mutating `tasks_*` tool returns this
 * object and nothing smaller.
 *
 * **`overdue` is deliberately not a field.** It is `due_at < now` and nothing else
 * (`ARCHITECTURE.md` §The task queue: "derived from `due_at`, never stored"), so a
 * caller holding `due_at` and a clock already has it — while a field would be a
 * second answer computed at a different instant from the one the reader is at.
 * `tasks_list` takes it as a **filter**, which is the one place the comparison has to
 * happen in SQL.
 */
export const TaskSchema = z
    .object({
    ref: TaskRefSchema,
    client: z
        .string()
        .nullable()
        .describe('The client slug this is for, or null for studio work that belongs to nobody.'),
    title: z.string().describe('One line, as it reads on the board.'),
    instructions: z.string().describe('What to do. Written for whoever picks it up.'),
    acceptance: z.string().describe('How to know it is done. Empty when it was left unsaid.'),
    queue: TaskQueueSchema,
    priority: TaskPrioritySchema,
    status: z
        .enum(taskMachine.states)
        .describe('open (nobody has it), claimed (taken, not started), in_progress, blocked (waiting ' +
        'on somebody), review (done and waiting for a person), done, cancelled.'),
    assignee_kind: AssigneeKindSchema,
    assignee_id: z
        .string()
        .nullable()
        .describe('The agent’s id or the person’s user id, or null when nobody holds it.'),
    /**
     * The name beside the id, so that a board can say who is on what.
     *
     * It travels **with** `assignee_id` rather than being looked up from a second
     * list, for the reason `CLAUDE.md` gives about pages — "a page composes
     * primitives, states facts it was given, and computes nothing". A `staff_list`
     * tool plus a client-side join would also race: a task assigned between the two
     * reads renders as an id nobody can name.
     *
     * One field for two kinds of holder, because `assignee_kind` already says which
     * and a second name column would be a second spelling of one fact. It is
     * **nullable for a reason that is not "nobody"** as well: a `user` row that has
     * gone leaves an id with no name, and "somebody holds this and we cannot name
     * them" is a different answer from "nobody holds this".
     */
    assignee_name: z
        .string()
        .nullable()
        .describe('What whoever holds it is called — a studio member’s name, or the agent’s own ' +
        'name. Read assignee_kind to know which of the two it is; an agent is not a ' +
        'person. Null when nobody holds it, and null when the holder’s row has gone.'),
    due_at: Rfc3339Schema.nullable().describe('When it is due, or null. A task is overdue when this is in the past and it is ' +
        'neither done nor cancelled.'),
    needs_review: z
        .boolean()
        .describe('True when completing it moves it to review for a person rather than straight to ' +
        'done. Anything with money attached is reviewed.'),
    decision: z
        .boolean()
        .describe('True when this task is a decision waiting on the client rather than account or ' +
        'finance admin. The studio clients list counts open account and finance tasks with ' +
        'this set as "waiting on a decision".'),
    money_ref: z
        .string()
        .nullable()
        .describe('The work order, extra or invoice the money on this task belongs to, or null.'),
    money: MoneySchema.nullable().describe('What it is worth, for the board’s money tag. Null when it carries no money.'),
    waiting_on: WaitingOnSchema.nullable().describe('Whose move it is, or null when not blocked.'),
    blocked_reason: z.string().nullable().describe('Why it is blocked, in a sentence, or null.'),
    context_refs: z
        .array(z.string())
        .describe('Everything this task is about, by reference. Read these before starting.'),
    outputs: z
        .array(TaskOutputSchema)
        .describe('What it produced, and what it was prepared to do.'),
    service_code: z
        .string()
        .nullable()
        .describe('The service this is work on — "video_editing" — or null.'),
    lease_expires_at: Rfc3339Schema.nullable().describe('When an agent’s claim on it lapses and it goes back to the queue; the agent extends ' +
        'it with tasks_heartbeat while it is still working. Null when nobody holds it, when ' +
        'a person does — a person’s task is theirs until they give it back — and whenever ' +
        'the task is not claimed or in progress: a blocked task is waiting, and holds no ' +
        'lease until it is unblocked.'),
    created_by_kind: z.enum(ASSIGNEE_KINDS).describe('Who raised it: user, agent, or none.'),
    created_by_id: z.string().nullable().describe('Their id, or null when the system raised it.'),
    completed_at: Rfc3339Schema.nullable().describe('When it was finished, or null.'),
    created_at: Rfc3339Schema.describe('When it was raised.'),
    updated_at: Rfc3339Schema.describe('When it last changed.'),
})
    .describe('One task: what to do, who holds it, what it is about and what it produced.');
export const COMMENT_VISIBILITIES = ['internal', 'client'];
export const CommentVisibilitySchema = z
    .enum(COMMENT_VISIBILITIES)
    .describe('Who may read this comment: internal is studio-only; client marks it fit for the client to ' +
    'read. No client page shows a task thread yet, so today neither reaches the client.');
export const TaskCommentSchema = z
    .object({
    id: z.string().describe('The comment’s id.'),
    actor_kind: z
        .enum(ASSIGNEE_KINDS)
        .describe('Who wrote it: user, agent, or none for the system.'),
    actor_id: z.string().nullable().describe('Their id, or null for the system.'),
    /** The same field, and the same reasons, as `TaskSchema.assignee_name`. */
    actor_name: z
        .string()
        .nullable()
        .describe('What they are called — a studio member’s name or the agent’s own. Null for the ' +
        'system and for an actor whose row has gone; actor_kind says which of the two ' +
        'kinds it was.'),
    body: z.string().describe('What they said.'),
    visibility: CommentVisibilitySchema.describe('internal — studio only. client — fit for the client to read; no client page shows it yet.'),
    at: Rfc3339Schema.describe('When they said it.'),
})
    .describe('One comment on a task. The thread is the task’s history in words.');
export const TimeEntrySchema = z
    .object({
    id: z.string().describe('The entry’s id.'),
    actor_kind: z.enum(ASSIGNEE_KINDS).describe('Who spent it: user or agent.'),
    actor_id: z.string().nullable().describe('Their id, or null for the system.'),
    /** The same field, and the same reasons, as `TaskSchema.assignee_name`. */
    actor_name: z
        .string()
        .nullable()
        .describe('What they are called — a studio member’s name or the agent’s own. Null for the ' +
        'system and for an actor whose row has gone; actor_kind says which of the two ' +
        'kinds it was.'),
    minutes: z.int().describe('How many minutes. Always positive.'),
    billable_kind: BillableKindSchema,
    at: Rfc3339Schema.describe('When the work was done.'),
})
    .describe('One block of time logged against a task. These minutes are what a client’s effective hourly rate is worked out from.');
/**
 * The lease `tasks_claim_next` takes: thirty minutes.
 *
 * `ARCHITECTURE.md` §The task queue: "leases one for 30 minutes. `tasks_heartbeat`
 * extends the lease; `tasks.releaseStaleLeases` returns abandoned ones." One
 * constant, read by the service that takes the lease and by the job that reclaims it.
 */
export const TASK_LEASE_MINUTES = 30;
/* ------------------------------------------------------------ who may hold one */
/**
 * One name `tasks_assign` will accept — a studio member, or an agent.
 *
 * The pair `(kind, id)` is exactly `tasks_assign`'s own `assignee_kind` and
 * `assignee_id`, in that order and with those meanings, so a caller reads a row off
 * this list and sends it straight back without translating anything. `none` is
 * deliberately **not** one of the kinds: taking a task off whoever has it is not
 * choosing a person, and a directory of people is the wrong place to offer it.
 *
 * `name` is not nullable, unlike `TaskSchema.assignee_name`. That field has to
 * describe a holder whose row has gone; this one is a directory, and a row with no
 * name is a row nothing could offer.
 */
export const TaskAssigneeSchema = z
    .object({
    kind: z
        .enum(['user', 'agent'])
        .describe('user for a studio member, agent for an agent. Send it back as assignee_kind.'),
    id: z.uuid().describe('Send it back as assignee_id, exactly as it is given here.'),
    name: z
        .string()
        .describe('What they are called — a person’s name, or the agent’s own lowercase name.'),
})
    .describe('Somebody a task can be given to.');
//# sourceMappingURL=tasks.js.map